/**
 * BehaviorAction - base class for behavior tree action nodes.
 */

import { type BehaviorNodeOptions } from '../BehaviorNode.js';
import { BehaviorStatus } from '../BehaviorNode.js';
import { LeafNode } from '../nodes/LeafNode.js';

import type { Actor } from '../../../actors/Actor.js';
import type { Blackboard } from '../blackboard/Blackboard.js';

export interface BehaviorActionOptions extends BehaviorNodeOptions {}

/** Base class for behavior tree action nodes. */
export abstract class BehaviorAction extends LeafNode {
  private hasEntered: boolean = false;

  constructor(options: BehaviorActionOptions = {}) {
    super(options);
  }

  protected getOwner(blackboard: Blackboard): Actor | null {
    return blackboard.getOwner();
  }

  protected getWorld(blackboard: Blackboard) {
    return this.getOwner(blackboard)?.getWorld();
  }

  protected abstract override onInitialize(blackboard: Blackboard): void;

  protected override async onExecute(blackboard: Blackboard, deltaTime: number): Promise<BehaviorStatus> {
    // Enter the action if not already entered
    if (!this.hasEntered) {
      this.onEnter(blackboard);
      this.hasEntered = true;
    }

    // Update the action
    const status = await this.onUpdate(blackboard, deltaTime);

    // Exit the action if it completed
    if (status !== BehaviorStatus.Running) {
      this.onExit(blackboard, status);
      this.hasEntered = false;
    }

    return status;
  }

  protected override onReset(): void {
    super.onReset();
    this.hasEntered = false;
  }

  protected onEnter(blackboard: Blackboard): void {}

  protected abstract onUpdate(blackboard: Blackboard, deltaTime: number): Promise<BehaviorStatus>;

  protected onExit(blackboard: Blackboard, status: BehaviorStatus): void {}
}